import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, FileText } from "lucide-react";
import { format, parseISO } from "date-fns";

/** Mounted globally inside Layout. If the user's accepted terms version differs
 *  from the currently-published version, blocks the UI with a modal until they
 *  re-accept (or sign out).
 */
export default function TermsReacceptDialog() {
    const { user, logout, refresh } = useAuth();
    const [current, setCurrent] = useState(null); // {version, notes}
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/terms/version").then((r) => setCurrent(r.data)).catch(() => {});
    }, [user]);

    if (!user || !current) return null;
    const accepted = user.terms_accepted_version;
    const needsReaccept = !accepted || accepted !== current.version;
    if (!needsReaccept) return null;

    const accept = async () => {
        setSubmitting(true);
        try {
            await api.post("/auth/accept-terms");
            await refresh();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true}>
            <DialogContent
                className="max-w-xl rounded-md border-slate-200 [&>button]:hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                data-testid="terms-reaccept-dialog"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-brand" strokeWidth={1.75} />
                        <DialogTitle className="font-heading text-2xl tracking-tight">Updated Terms & Licence Agreement</DialogTitle>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                        We've published a new version of the Flexible Office Pod Licence Agreement.
                        Please review and accept it to continue using your account.
                    </p>
                </DialogHeader>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                        <span className="text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500">Effective</span>
                        <span className="font-mono text-slate-900">{format(parseISO(current.version), "MMM d, yyyy · HH:mm 'UTC'")}</span>
                    </div>
                    {current.notes && (
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500">What changed</p>
                            <p className="text-sm text-slate-700 mt-1 leading-relaxed">{current.notes}</p>
                        </div>
                    )}
                </div>
                <ScrollArea className="h-48 rounded-sm border border-slate-200 p-4 bg-white">
                    <p className="text-xs uppercase tracking-[0.18em] font-mono text-slate-500 mb-2">Summary</p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        The Agreement covers the credit-based booking system, cancellation rules, time-overrun policy,
                        acceptable use, damage liability, confidentiality, indemnity and governing law. Continued use of
                        the workspace constitutes acceptance of the latest published version.
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed mt-3">
                        For the full text, open the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brand font-medium hover:underline underline-offset-4 inline-flex items-center gap-1">Flexible Office Pod Licence Agreement <ExternalLink className="h-3 w-3" strokeWidth={2} /></Link> in a new tab.
                    </p>
                </ScrollArea>
                <div className="flex items-start gap-3 pt-1">
                    <Checkbox
                        id="reaccept-terms"
                        checked={checked}
                        onCheckedChange={(v) => setChecked(!!v)}
                        data-testid="reaccept-checkbox"
                        className="mt-0.5"
                    />
                    <label htmlFor="reaccept-terms" className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
                        I have read and agree to the updated <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brand font-medium hover:underline underline-offset-4">Flexible Office Pod Licence Agreement</Link>.
                    </label>
                </div>
                <div className="flex justify-between items-center gap-2 pt-2 flex-wrap">
                    <Button variant="ghost" size="sm" onClick={logout} data-testid="reaccept-logout-btn" className="text-slate-500 hover:text-slate-900">
                        Sign out instead
                    </Button>
                    <Button
                        disabled={!checked || submitting}
                        onClick={accept}
                        data-testid="reaccept-confirm-btn"
                        className="bg-brand hover:bg-brand-hover text-white rounded-md"
                    >
                        {submitting ? "Saving…" : "Accept & continue"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
